/**
 * WEEX REST API Functions
 *
 * REST endpoint helpers for WEEX v3 spot market data.
 *
 * Endpoints/params/shapes mirror the production WEEX connector
 * (gitlab.1wq.net/mm/111-connman → connector/weex/market/rest/service.go).
 *
 * @see https://api-spot.weex.com
 */

import { weexChangePercent } from './protocol';
import type {
  WeexDepthRequest,
  WeexDepthResponse,
  WeexKlinesRequest,
  WeexKlinesResponse,
  WeexSymbolsRequest,
  WeexSymbolsResponse,
  WeexTickerRequest,
  WeexTickerResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api-spot.weex.com';

/**
 * WEEX API success code.
 */
export const WEEX_SUCCESS_CODE = '00000';

/**
 * WEEX REST API endpoints.
 * @see connector/weex/market/rest/service.go
 */
export const weexRestEndpoints = {
  ticker: `${BASE_URL}/api/v3/market/ticker/24hr`,
  symbols: `${BASE_URL}/api/v3/exchangeInfo`,
  depth: `${BASE_URL}/api/v3/market/depth`,
  klines: `${BASE_URL}/api/v3/market/klines`,
} as const;

/**
 * Format a base/quote pair into a WEEX symbol.
 * WEEX v3 uses unsuffixed symbols, e.g. "BTCUSDT".
 *
 * @example
 * formatWeexSymbol('eth', 'usdt') // "ETHUSDT"
 */
export function formatWeexSymbol(base: string, quote: string): string {
  return `${base}${quote}`.toUpperCase();
}

/**
 * Parse a WEEX symbol like "BTCUSDT_SPBL" into base/quote.
 * Requires known quote currencies as WEEX doesn't use separators.
 *
 * @example
 * parseWeexSymbol('BTCUSDT_SPBL') // { base: 'BTC', quote: 'USDT' }
 */
export function parseWeexSymbol(
  symbol: string,
  knownQuotes: string[] = ['USDT', 'USDC', 'TUSD', 'FDUSD', 'BTC', 'ETH', 'BNB', 'TRY', 'EUR'],
): { base: string; quote: string } | null {
  const upper = symbol.toUpperCase().replace(/_SPBL$/, '');
  const sortedQuotes = [...knownQuotes].sort((a, b) => b.length - a.length);

  for (const quote of sortedQuotes) {
    if (upper.endsWith(quote)) {
      const base = upper.slice(0, -quote.length);
      if (base.length > 0) {
        return { base, quote };
      }
    }
  }

  return null;
}

/**
 * Fetches symbol information including tick size / step size / min trade amount.
 * WEEX returns the data as an array even for a single-symbol query.
 * @see https://api-spot.weex.com/api/v3/exchangeInfo
 */
export const getWeexSymbols = async (
  params?: WeexSymbolsRequest,
  options?: RestRequestOptions,
): Promise<WeexSymbolsResponse> => {
  const response = await get<
    { symbols: WeexSymbolsResponse['data']; code?: number; msg?: string },
    WeexSymbolsRequest | undefined
  >({
    url: weexRestEndpoints.symbols,
    params,
    options,
  });

  assertWeexSuccess(response.data);
  return {
    code: WEEX_SUCCESS_CODE,
    msg: '',
    data: response.data.symbols.map(row => ({
      ...row,
      baseCoin: String(row.baseAsset ?? row.baseCoin ?? ''),
      quoteCoin: String(row.quoteAsset ?? row.quoteCoin ?? ''),
      minTradeAmount: String(row.minTradeAmount),
      maxTradeAmount: String(row.maxTradeAmount),
    })),
  };
};

/**
 * Fetches 24-hour ticker statistics for a symbol.
 * @see https://api-spot.weex.com/api/v3/market/ticker/24hr
 */
export const getWeexTicker = async (
  params: WeexTickerRequest,
  options?: RestRequestOptions,
): Promise<WeexTickerResponse> => {
  const response = await get<WeexTickerV3, WeexTickerRequest>({
    url: weexRestEndpoints.ticker,
    params,
    options,
  });

  assertWeexSuccess(response.data);
  const row = response.data;
  return {
    code: WEEX_SUCCESS_CODE,
    msg: '',
    data: {
      ...row,
      high: row.highPrice,
      low: row.lowPrice,
      open: row.openPrice,
      close: row.lastPrice,
      size: row.volume,
      value: row.quoteVolume,
      ts: row.closeTime,
      priceChangePercent: weexChangePercent(row.lastPrice, row.openPrice, row.priceChangePercent),
    },
  };
};

/**
 * Fetches orderbook depth for a symbol.
 *
 * Uses the v3 default depth when no limit is supplied.
 * @see https://api-spot.weex.com/api/v3/market/depth
 */
export const getWeexDepth = async (
  params: WeexDepthRequest,
  options?: RestRequestOptions,
): Promise<WeexDepthResponse> => {
  const response = await get<
    WeexDepthResponse['data'] & { code?: number; msg?: string },
    WeexDepthRequest
  >({
    url: weexRestEndpoints.depth,
    params,
    options,
  });

  assertWeexSuccess(response.data);
  return { code: WEEX_SUCCESS_CODE, msg: '', data: response.data };
};

/**
 * Fetches candlestick (kline) data for a symbol.
 *
 * Maps the internal period option to the v3 interval query parameter.
 * Response is normalized to the existing SDK candle DTO.
 * @see https://api-spot.weex.com/api/v3/market/klines
 */
export const getWeexKlines = async (
  params: WeexKlinesRequest,
  options?: RestRequestOptions,
): Promise<WeexKlinesResponse> => {
  const response = await get<Array<Array<string | number>>, { symbol: string; interval: string }>({
    url: weexRestEndpoints.klines,
    params: { symbol: params.symbol, interval: params.period },
    options,
  });

  if (!Array.isArray(response.data)) throw new Error('Invalid WEEX v3 klines response');
  // Keep the SDK candle DTO stable: quote volume follows base volume, not close time.
  return {
    code: WEEX_SUCCESS_CODE,
    msg: '',
    data: response.data.map(row => [...row.slice(0, 6), row[7]]),
  };
};

/** Reject v3 exchange error bodies before creating internal SDK success envelopes. */
function assertWeexSuccess(body: { code?: number; msg?: string }) {
  if (body.code != null && body.code !== 0) throw new Error(body.msg ?? `WEEX error ${body.code}`);
}

/** Raw v3 ticker fields; the SDK normalizes these for existing display consumers. */
interface WeexTickerV3 {
  code?: number;
  msg?: string;
  symbol: string;
  lastPrice: string;
  highPrice: string;
  lowPrice: string;
  openPrice: string;
  volume: string;
  quoteVolume: string;
  priceChange: string;
  priceChangePercent: string;
  closeTime: number;
}
